function _define_property(obj, key, value) {
    if (key in obj) {
        Object.defineProperty(obj, key, {
            value: value,
            enumerable: true,
            configurable: true,
            writable: true
        });
    } else {
        obj[key] = value;
    }
    return obj;
}
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { PureComponent } from "react";
import classnames from "classnames";
import { withGenericConfig } from "/contexts/generic-config-context";
import { ENGLISH_LOCALE_ID } from "constants/localization";
import EarthIcon from "static/svg/earth.svg";
import ArrowDown from "static/svg/arrow-down.svg";
import { generateOnKeyDownHandler } from "/client/services/accessibility/accessibility-service";
import { trackEvent } from "services/bigbrain-service";
import { LANGUAGE_PICKER_OPENED } from "constants/bigbrain-event-types";
import * as Sentry from "@sentry/nextjs";
import LanguagePickerDialogComponent from "./language-picker-dialog/language-picker-dialog-component";
import { getLanguagesByIds, LANGUAGES, MAIN_LANGUAGES_VALUES } from "./language-picker-service";
import { StyledLanguagePickerComponent } from "./language-picker-component.scss";
const MIN_WINDOW_DISTANCE = 200;
const LEFT_DIRECTION = "left";
const RIGHT_DIRECTION = "right";
const MAX_LANGUAGES_ROWS = 5;
class LanguagePickerComponent extends PureComponent {
    componentDidMount() {
        window.addEventListener("resize", this.getHorizontalDirection);
        this.getHorizontalDirection();
    }
    componentWillUnmount() {
        window.removeEventListener("resize", this.getHorizontalDirection);
    }
    getHorizontalDirection() {
        if (!this.selector) return;
        let horizontalDirection = LEFT_DIRECTION;
        const horizontalPosition = this.selector.getBoundingClientRect().right;
        if ((window.innerWidth || document.documentElement.clientWidth) - horizontalPosition < MIN_WINDOW_DISTANCE) {
            horizontalDirection = RIGHT_DIRECTION;
        }
        this.setState({
            horizontalDirection
        });
    }
    getShortLabel() {
        const { selectedLanguageId } = this.state;
        const selectedLanguage = LANGUAGES[selectedLanguageId];
        const { shortLabel } = selectedLanguage;
        return shortLabel.toUpperCase();
    }
    render() {
        const { isDialogOpen, selectedLanguageId } = this.state;
        const { dialogDirection, useShortLabels, translate, showLabel } = this.props;
        let selectedLanguage = LANGUAGES[selectedLanguageId];
        if (!selectedLanguage) {
            Sentry.captureMessage(`Selected language Id is: ${selectedLanguageId}, and it is not supported in the language picker.`);
            selectedLanguage = LANGUAGES[ENGLISH_LOCALE_ID];
        }
        const { label } = selectedLanguage;
        const { horizontalDirection } = this.state;
        const labelToShow = useShortLabels ? this.getShortLabel() : label;
        return /*#__PURE__*/ _jsxs(StyledLanguagePickerComponent, {
            className: classnames("language-picker-component", {
                "is-open": isDialogOpen
            }),
            ref: (el)=>{
                this.selector = el;
            },
            tabIndex: 0,
            "aria-haspopup": "true",
            "aria-expanded": isDialogOpen,
            "aria-label": translate("languagePicker.chooseLanguageLabel"),
            onKeyDown: this.onKeyDown,
            role: "combobox",
            children: [
                /*#__PURE__*/ _jsxs("div", {
                    className: "selected-language ignore-clickoutside",
                    onClick: this.toggleDialog,
                    children: [
                        /*#__PURE__*/ _jsx(EarthIcon, {
                            className: "earth-icon"
                        }),
                        showLabel && /*#__PURE__*/ _jsx("div", {
                            className: "language-label",
                            children: labelToShow
                        }),
                        /*#__PURE__*/ _jsx(ArrowDown, {
                            className: "arrow-down-icon"
                        })
                    ]
                }),
                /*#__PURE__*/ _jsx("div", {
                    className: classnames("language-picker-dialog-component-wrapper", dialogDirection, horizontalDirection),
                    style: {
                        "--number-of-lang-rows": Math.min(this.languages.length, MAX_LANGUAGES_ROWS)
                    },
                    children: /*#__PURE__*/ _jsx(LanguagePickerDialogComponent, {
                        languages: this.languages,
                        selectedLanguageId: selectedLanguageId,
                        closeDialog: this.closeDialog,
                        outsideClickIgnoreClass: "ignore-clickoutside"
                    })
                })
            ]
        });
    }
    constructor(props){
        super(props);
        _define_property(this, "openDialog", ()=>{
            const { selectedLanguageId } = this.state;
            trackEvent(LANGUAGE_PICKER_OPENED, {
                info1: selectedLanguageId
            });
            this.setState({
                isDialogOpen: true
            });
        });
        _define_property(this, "closeDialog", ()=>{
            this.setState({
                isDialogOpen: false
            });
        });
        _define_property(this, "toggleDialog", ()=>{
            const { isDialogOpen } = this.state;
            if (isDialogOpen) {
                this.closeDialog();
            } else {
                this.openDialog();
            }
        });
        this.state = {
            isDialogOpen: false,
            selectedLanguageId: props.userSelectedLanguage || props.localeId || ENGLISH_LOCALE_ID,
            horizontalDirection: LEFT_DIRECTION
        };
        const defaultLanguages = props.showMainLanguagesOnly ? MAIN_LANGUAGES_VALUES : Object.values(LANGUAGES);
        const overrideLanguages = props.overrideLanguages ? getLanguagesByIds(props.overrideLanguages) : null;
        this.languages = overrideLanguages && overrideLanguages.length > 0 ? overrideLanguages : defaultLanguages;
        this.getHorizontalDirection = this.getHorizontalDirection.bind(this);
        this.onKeyDown = generateOnKeyDownHandler(this.toggleDialog);
    }
}
LanguagePickerComponent.defaultProps = {
    localeId: ENGLISH_LOCALE_ID,
    useShortLabels: false,
    showLabel: true
};
export default withGenericConfig(LanguagePickerComponent);
